import axiosInstance from "@/axios";

export const state = {
    plansData: [],
    plansLoading: false,
    plansMsg: null,
    errorMsg: null,
    successMsg: null,
    pagination: {
        currentPage: 1,
        totalPages: 1,
        totalItems: 0,
        itemsPerPage: 10,
        hasNext: false,
        hasPrev: false
    }
}

export const mutations = {
    SET_PLANS_DATA(state, plansData) {
        state.plansData = plansData
    },
    SET_LOADING(state, plansLoading) {
        state.plansLoading = plansLoading
    },
    SET_PLANS_MSG(state, plansMsg) {
        state.plansMsg = plansMsg
    },
    SET_PAGINATION(state, pagination) {
        state.pagination = pagination
    },
    UPDATE_PLAN(state, updatedPlan) {
        if (Array.isArray(state.plansData)) {
            const index = state.plansData.findIndex(plan => plan.id === updatedPlan.id);
            if (index !== -1) {
                state.plansData.splice(index, 1, { ...state.plansData[index], ...updatedPlan });
            }
        }
    },
    ADD_PLAN(state, plan) {
        if (Array.isArray(state.plansData)) {
            state.plansData.unshift(plan);
        }
    },
    DELETE_PLAN(state, planId) {
        if (Array.isArray(state.plansData)) {
            state.plansData = state.plansData.filter(plan => plan.id !== planId);
        }
    },
    SET_ERROR_MSG(state, errorMsg) {
        state.errorMsg = errorMsg
    },
    SET_SUCCESS_MSG(state, successMsg) {
        state.successMsg = successMsg
    },
}

export const getters = {
    plansData: state => state.plansData,
    plansLoading: state => state.plansLoading,
    plansMsg: state => state.plansMsg,
    plansPagination: state => state.pagination,
    errorMsg: state => state.errorMsg,
    successMsg: state => state.successMsg,
}

export const actions = {
    async getPlansData({ commit }, params = {}) {
        const { page = 1, perPage = 10, ...filterParams } = params;

        commit('SET_LOADING', true)
        try {
            const responseData = await axiosInstance({ requiresAuth: true }).get(`/agent/subscription-plans`, {
                params: { page, perPage, ...filterParams }
            });

            let apiData = responseData;

            // Handle wrapped responses: { success: true, data: { data: [...], meta: {...} } }
            if (responseData.data && typeof responseData.data === 'object' && !Array.isArray(responseData.data) && responseData.data.data) {
                apiData = responseData.data;
            }

            let plansData;
            let paginationData = {
                currentPage: page,
                totalPages: 1,
                totalItems: 0,
                itemsPerPage: perPage,
                hasNext: false,
                hasPrev: false
            };

            if (apiData.data && Array.isArray(apiData.data) && apiData.meta) {
                plansData = apiData.data;
                const meta = apiData.meta;
                paginationData = {
                    currentPage: parseInt(meta.current_page) || page,
                    totalPages: parseInt(meta.last_page) || 1,
                    totalItems: parseInt(meta.total) || plansData.length,
                    itemsPerPage: parseInt(meta.per_page) || perPage,
                    hasNext: meta.current_page < meta.last_page,
                    hasPrev: meta.current_page > 1
                };
            } else if (Array.isArray(apiData)) {
                plansData = apiData;
                paginationData = {
                    currentPage: 1,
                    totalPages: 1,
                    totalItems: plansData.length,
                    itemsPerPage: plansData.length,
                    hasNext: false,
                    hasPrev: false
                };
            } else {
                plansData = apiData || [];
                paginationData.totalItems = plansData.length;
            }

            commit('SET_PLANS_DATA', plansData);
            commit('SET_PAGINATION', paginationData);
            commit('SET_PLANS_MSG', null);
        } catch (error) {
            commit('SET_PLANS_MSG', error.response?.data?.message || 'Failed to fetch subscription plans');
        } finally {
            commit('SET_LOADING', false);
        }
    },

    async createPlan({ commit }, payload) {
        commit('SET_ERROR_MSG', null);
        commit('SET_SUCCESS_MSG', null);
        commit('SET_LOADING', true);
        try {
            const response = await axiosInstance({ requiresAuth: true })
                .post(`/agent/subscription-plans`, payload);

            const created = response?.data ?? response;
            commit('ADD_PLAN', created);
            commit('SET_SUCCESS_MSG', 'Plan created successfully');
            return Promise.resolve(created);
        } catch (error) {
            const errorMessage = error.response?.data?.message || 'An error occurred while creating the plan';
            commit('SET_ERROR_MSG', errorMessage);
            return Promise.reject(error);
        } finally {
            commit('SET_LOADING', false);
        }
    },

    async updatePlan({ commit }, { uuid, payload }) {
        commit('SET_ERROR_MSG', null);
        commit('SET_SUCCESS_MSG', null);
        try {
            const response = await axiosInstance({ requiresAuth: true })
                .put(`/agent/subscription-plans/${uuid}`, payload);

            const updated = response?.data ?? response;
            commit('UPDATE_PLAN', { id: uuid, ...payload, ...updated });
            commit('SET_SUCCESS_MSG', 'Plan updated successfully');
            return Promise.resolve(updated);
        } catch (error) {
            const errorMessage = error.response?.data?.message || 'An error occurred while updating the plan';
            commit('SET_ERROR_MSG', errorMessage);
            return Promise.reject(error);
        }
    },

    async deletePlan({ commit }, uuid) {
        commit('SET_LOADING', true);
        try {
            const response = await axiosInstance({ requiresAuth: true })
                .delete(`/agent/subscription-plans/${uuid}`);

            commit('DELETE_PLAN', uuid);
            commit('SET_PLANS_MSG', 'Plan deleted successfully');
            return Promise.resolve(response);
        } catch (error) {
            const errorMessage = error.response?.data?.message || 'An error occurred while deleting the plan';
            commit('SET_PLANS_MSG', errorMessage);
            return Promise.reject(error);
        } finally {
            commit('SET_LOADING', false);
        }
    },

    async togglePlanActive({ commit }, uuid) {
        try {
            const response = await axiosInstance({ requiresAuth: true })
                .patch(`/agent/subscription-plans/${uuid}/toggle-active`);

            // Unwrap nested response: { success, data: { ...plan } } or { success, data: { data: { ...plan } } }
            let planData = response?.data ?? response;
            if (planData && planData.data && typeof planData.data === 'object' && !Array.isArray(planData.data)) {
                planData = planData.data;
            }
            if (planData && planData.data && typeof planData.data === 'object' && !Array.isArray(planData.data)) {
                planData = planData.data;
            }

            commit('UPDATE_PLAN', planData);
            return Promise.resolve(planData);
        } catch (error) {
            const errorMessage = error.response?.data?.message || 'An error occurred while toggling the plan';
            commit('SET_ERROR_MSG', errorMessage);
            return Promise.reject(error);
        }
    },

    async getPlan(_, uuid) {
        const response = await axiosInstance({ requiresAuth: true })
            .get(`/agent/subscription-plans/${uuid}`);
        return response?.data ?? response;
    },
}
